import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPen, faCheck, faTimes, faGripVertical, faPlus } from "@fortawesome/free-solid-svg-icons";
import CurrencyComponent from "../CurrencyComponent";
import {
  normalizeLayoutSegments,
  patchItemWithLayoutSegments,
  addTextSegmentToItem,
  SEGMENT_COMPOSITION,
} from "helper/briqueLayoutSegments";
import { articleSegmentsDroppableId } from "helper/briqueDragDropIds";

/**
 * Composant d'affichage/édition d'un item de type "Produit" dans une brique devis.
 *
 * Affiche :
 * - Le nom du produit (éditable)
 * - Le prix unitaire (éditable)
 * - La quantité (éditable)
 * - Un texte libre optionnel
 */
const BriqueItemProduct = ({
  item,
  briqueDevisId,
  onUpdate,
  onRemove,
  dragHandleProps = {},
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue]         = useState(
    item.name_override || item.renammed || item.effective_name || ""
  );

  useEffect(() => {
    setNameValue(item.name_override || item.renammed || item.effective_name || "");
  }, [item.name_override, item.renammed, item.effective_name]);

  const handleNameSave = () => {
    onUpdate({ ...item, name_override: nameValue });
    setIsEditingName(false);
  };

  const layoutSegments = useMemo(() => normalizeLayoutSegments(item), [
    item.layout_segments,
    item.text,
    item._localId,
  ]);
  const layoutDndType = `LAY_ARTICLE_${item._localId}`;
  const segmentsDroppableId = articleSegmentsDroppableId(briqueDevisId, item._localId);

  const updateSegmentText = useCallback(
    (segId, text) => {
      const next = layoutSegments.map((s) =>
        s._localId === segId ? { ...s, text } : s
      );
      onUpdate(patchItemWithLayoutSegments(item, next));
    },
    [item, layoutSegments, onUpdate]
  );

  const removeTextSegment = useCallback(
    (segId) => {
      const next = layoutSegments.filter(
        (s) => s.type === SEGMENT_COMPOSITION || s._localId !== segId
      );
      onUpdate(patchItemWithLayoutSegments(item, next));
    },
    [item, layoutSegments, onUpdate]
  );

  const handleAddTextSegment = useCallback(() => {
    onUpdate(addTextSegmentToItem(item, layoutSegments.length));
  }, [item, layoutSegments.length, onUpdate]);

  const handleFieldChange = (field, value) => {
    onUpdate({ ...item, [field]: value === "" ? null : Number(value) });
  };

  return (
    <div className="brique-item brique-item--product mb-3 border rounded p-0 bg-white">
      {/* Header ligne produit */}
      <Row className="align-items-center mx-0 border-bottom py-2 px-3 bg-light">
        {/* Handle de drag */}
        <Col xs="auto" className="pl-0 pr-2 text-muted" {...dragHandleProps} style={{ cursor: "grab" }}>
          <FontAwesomeIcon icon={faGripVertical} />
        </Col>

        {/* Nom du produit (éditable) */}
        <Col>
          {isEditingName ? (
            <div className="d-flex align-items-center" style={{ gap: '0.25rem' }}>
              <Form.Control
                size="sm"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                autoFocus
              />
              <Button size="sm" variant="success" onClick={handleNameSave}>
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setIsEditingName(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : (
            <span
              onClick={() => setIsEditingName(true)}
              style={{ cursor: "pointer", fontWeight: 600 }}
              title="Cliquer pour modifier le nom"
            >
              {nameValue || item.effective_name || "Produit sans nom"}
              <FontAwesomeIcon icon={faPen} className="ml-2 text-muted" style={{ fontSize: "0.75rem" }} />
            </span>
          )}
        </Col>

        {/* Prix unitaire */}
        <Col xs="3">
          <Form.Group className="mb-0">
            <CurrencyComponent
              initialPrice={item.unit_price}
              onPriceChange={(val) => handleFieldChange("unit_price", val)}
              nullable
            />
          </Form.Group>
        </Col>

        {/* Quantité */}
        <Col xs="2">
          <Form.Group className="mb-0">
            <Form.Control
              size="sm"
              type="number"
              value={item.quantity ?? ""}
              onChange={(e) => handleFieldChange("quantity", e.target.value)}
              placeholder="Qté"
              min="0.01"
              step="0.01"
              style={{ width: "90px" }}
            />
          </Form.Group>
        </Col>

        {/* Total calculé */}
        {/* {item.unit_price != null && item.quantity != null && (
          <Col xs="auto">
            <b style={{ fontSize: "0.9rem", whiteSpace: "nowrap" }}>
              soit {((item.unit_price * item.quantity) / 100).toFixed(2)} €
            </b>
          </Col>
        )} */}

        {/* Bouton supprimer */}
        <Col xs="1">
          <Button size="sm" variant="outline-danger" onClick={onRemove} title="Supprimer cet item">
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Col>
      </Row>

      <div className="px-3 py-2">
        <Droppable droppableId={segmentsDroppableId} type={layoutDndType}>
          {(dropProvided) => (
            <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
              {layoutSegments.map((seg, segIndex) => (
                <Draggable
                  key={seg._localId}
                  draggableId={`article-seg-${item._localId}-${seg._localId}`}
                  index={segIndex}
                  type={layoutDndType}
                >
                  {(dragProvided) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className="mb-2"
                    >
                      {seg.type === SEGMENT_COMPOSITION ? (
                        <div {...dragProvided.dragHandleProps}>
                        </div>
                      ) : (
                        <div className="border rounded p-2 bg-light">
                          <div className="d-flex align-items-start" style={{ gap: "0.35rem" }}>
                            <span
                              className="text-muted pt-1"
                              {...dragProvided.dragHandleProps}
                              style={{ cursor: "grab", flexShrink: 0 }}
                              title="Glisser pour réordonner"
                            >
                              <FontAwesomeIcon icon={faGripVertical} />
                            </span>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              size="sm"
                              value={seg.text || ""}
                              onChange={(e) => updateSegmentText(seg._localId, e.target.value)}
                              placeholder="Texte — avant ou après le bloc article sur le devis / facture"
                              style={{ resize: "vertical" }}
                            />
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="flex-shrink-0"
                              onClick={() => removeTextSegment(seg._localId)}
                              title="Supprimer ce texte"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </div>

      <div className="px-3 pb-2 d-flex" style={{ gap: "0.5rem" }}>
        <Button size="sm" variant="outline-secondary" onClick={handleAddTextSegment}>
          <FontAwesomeIcon icon={faPlus} className="mr-1" />
          Texte
        </Button>
      </div>
    </div>
  );
};

export default BriqueItemProduct;
